import { PromptCategory } from '@common/model/admin/thirdPartyApi';
import { coreApi } from '@cp/shared/api/core';
import { ThirdPartyApiTags, thirdPartyApiTagTypes } from './tags';

export interface Prompt {
  id: number;
  name: string;
  content: string;
  category: PromptCategory;
  attachmentId?: number;
  isDefault?: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface ThirdPartyApiSettings {
  id: number;
  apiKey: string;
  model: string;
  textModel: string;
  url: string;
  createdAt: string;
  updatedAt: string;
}

export interface GenerateImageRequest {
  imageUrl?: string;
  prompt: string;
  clientId: number;
  attachmentId?: number;
}

export interface GenerateImageCreateResponse {
  taskId: string;
}

export type GenerateImageStatusResponse =
  | { status: 'pending' }
  | { status: 'success'; imageBase64: string; generatedImageUrl: string; balance: number }
  | { status: 'fail'; message: string };

export interface BalanceResponse {
  balance: number;
}

export interface UploadImageResponse {
  attachmentId: number;
}
export const thirdPartyApiApi = coreApi.enhanceEndpoints({ addTagTypes: thirdPartyApiTagTypes }).injectEndpoints({
  endpoints: (builder) => ({
    getPrompts: builder.query<Prompt[], void>({
      query: () => ({
        url: 'admin/third-party-api/prompts',
        method: 'get',
      }),
      providesTags: [ThirdPartyApiTags.Prompt],
    }),

    getApiSettings: builder.query<ThirdPartyApiSettings | null, void>({
      query: () => ({
        url: 'admin/third-party-api/settings',
        method: 'get',
      }),
      providesTags: [ThirdPartyApiTags.ThirdPartyApiSettings],
    }),

    createApiSettings: builder.mutation<
      ThirdPartyApiSettings,
      { apiKey: string; model: string; textModel: string; url: string }
    >({
      query: (settings) => ({
        url: 'admin/third-party-api/settings',
        method: 'post',
        body: settings,
      }),
      invalidatesTags: [ThirdPartyApiTags.ThirdPartyApiSettings],
    }),

    createPrompt: builder.mutation<
      Prompt,
      { name: string; content: string; category?: PromptCategory; attachmentId?: number; isDefault?: boolean }
    >({
      query: (prompt) => ({
        url: 'admin/third-party-api/prompts',
        method: 'post',
        body: prompt,
      }),
      invalidatesTags: [ThirdPartyApiTags.Prompt],
    }),

    updatePrompt: builder.mutation<
      Prompt,
      { id: number; name: string; content: string; category?: PromptCategory; attachmentId?: number; isDefault?: boolean }
    >({
      query: ({ id, ...prompt }) => ({
        url: `admin/third-party-api/prompts/${id}`,
        method: 'put',
        body: prompt,
      }),
      invalidatesTags: [ThirdPartyApiTags.Prompt],
    }),

    deletePrompt: builder.mutation<{ success: boolean }, number>({
      query: (id) => ({
        url: `admin/third-party-api/prompts/${id}`,
        method: 'delete',
      }),
      invalidatesTags: [ThirdPartyApiTags.Prompt],
    }),

    generateImage: builder.mutation<GenerateImageCreateResponse, GenerateImageRequest>({
      query: (request) => ({
        url: 'admin/third-party-api/generate-image',
        method: 'post',
        body: request,
      }),
    }),

    getGenerateImageStatus: builder.query<GenerateImageStatusResponse, string>({
      query: (taskId) => ({
        url: `admin/third-party-api/generate-image/${encodeURIComponent(taskId)}`,
        method: 'get',
      }),
    }),

    getBalance: builder.query<BalanceResponse, void>({
      query: () => ({
        url: 'admin/third-party-api/balance',
        method: 'get',
      }),
      providesTags: [ThirdPartyApiTags.Balance],
    }),

    uploadImage: builder.mutation<UploadImageResponse, FormData>({
      query: (formData) => ({
        url: 'admin/third-party-api/upload-image',
        method: 'post',
        body: formData,
        formData: true,
      }),
    }),
  }),
});
